import React, { FC, useState } from "react";
import { format, isValid, parseISO, startOfToday } from "date-fns";
import VKButton from "components/vkButton";
import { dateFormats } from "utils/formats";
import { LocalizeText } from "components/localizer";
import { TFormEvent } from "utils/types";
import { List, ListItem, ListItemText, Box } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { DatePicker } from "components/datePicker";
import { ISubscription } from "reducers/subscriptions";

interface IPauseDates {
    pauseStartDate: string | null;
    pauseEndDate: string | null;
}

interface IProps {
    onSubmit: (dates: IPauseDates) => void;
    subscription: ISubscription;
}

export const PauseSubscriptionDatePicker: FC<IProps> = ({ onSubmit, subscription }) => {
    const isActivePause =
        !!subscription.pause_start_date &&
        parseISO(subscription.pause_start_date) <= startOfToday();

    const [startDate, setStartDate] = useState<Date | null>(
        subscription.pause_start_date ? parseISO(subscription.pause_start_date) : null
    );
    const [endDate, setEndDate] = useState<Date | null>(
        subscription.pause_end_date ? parseISO(subscription.pause_end_date) : null
    );
    const [startDateModified, setStartDateModified] = useState(false);

    const isFirstOfMonth = (date: Date): boolean => date.getDate() === 1;

    const isClearing = !startDate && !endDate;

    // Only enforce first-of-month / future rules if the user actually changed the start date
    const startDateValid =
        isActivePause ||
        !startDate ||
        !startDateModified ||
        (isValid(startDate) && isFirstOfMonth(startDate) && startDate >= startOfToday());

    const endDateValid =
        !endDate ||
        (!!startDate && isValid(endDate) && endDate > startDate);

    const formIsValid = isClearing || (startDateValid && endDateValid);

    const handleChangeStart = (newDate: Date | null): void => {
        setStartDate(newDate);
        setStartDateModified(true);
    };

    const handleClearStart = (): void => {
        setStartDate(null);
        setEndDate(null);
        setStartDateModified(true);
    };

    const handleSubmit = (event: TFormEvent): void => {
        event.preventDefault();
        if (formIsValid) {
            onSubmit({
                pauseStartDate: startDate ? format(startDate, dateFormats.WEBDATE) : null,
                pauseEndDate: endDate ? format(endDate, dateFormats.WEBDATE) : null,
            });
        }
    };

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <List>
                    <ListItem>
                        <DatePicker
                            label={<LocalizeText tag="pauseStartDate" />}
                            value={startDate}
                            format={dateFormats.WEBDATE}
                            minDate={startOfToday()}
                            shouldDisableDate={(date) => (date as Date).getDate() !== 1}
                            disabled={isActivePause}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!startDate && !startDateValid,
                                },
                            }}
                            onChange={(newDate) => handleChangeStart(newDate as Date | null)}
                        />
                        {!isActivePause && (
                            <VKButton
                                variant="outlined"
                                sx={{ marginBottom: "auto", marginLeft: "5px", marginTop: "-1px", height: "60px", width: "60px" }}
                                onClick={handleClearStart}
                            >
                                <Clear />
                            </VKButton>
                        )}
                    </ListItem>
                    <ListItem>
                        <DatePicker
                            label={<LocalizeText tag="pauseEndDate" />}
                            value={endDate}
                            format={dateFormats.WEBDATE}
                            minDate={startDate ?? startOfToday()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!endDate && !endDateValid,
                                },
                            }}
                            onChange={(newDate) => setEndDate(newDate as Date | null)}
                        />
                        <VKButton
                            variant="outlined"
                            sx={{ marginBottom: "auto", marginLeft: "5px", marginTop: "-1px", height: "60px", width: "60px" }}
                            onClick={() => setEndDate(null)}
                        >
                            <Clear />
                        </VKButton>
                    </ListItem>
                    {isActivePause && (
                        <ListItem>
                            <ListItemText
                                secondary={<LocalizeText tag="pauseStartDateLockedInfo" />}
                            />
                        </ListItem>
                    )}
                    <ListItem>
                        <VKButton
                            type="submit"
                            tag="confirm"
                            disabled={!formIsValid}
                            template="primary"
                        />
                    </ListItem>
                </List>
            </form>
        </Box>
    );
};
